import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

interface ReservationSuccess {
  amenityName: string;
  date: string;
  startTime: string;
  endTime: string;
}

interface ReservationFailure {
  amenityId: string;
  amenityName: string;
  date: string;
  startTime: string;
  endTime: string;
  reason: string;
}

interface ReservationResult {
  success: boolean;
  totalAttempted: number;
  totalSuccessful: number;
  totalFailed: number;
  successes?: ReservationSuccess[];
  failures: ReservationFailure[];
}

interface ReservationResultsDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: 'session' | 'program' | 'groupPlay' | 'coachingProgram';
  entityName: string;
  operationType: 'create' | 'update' | 'rebook';
  reservationResult: ReservationResult | null;
}

const ReservationResultsDialog: React.FC<ReservationResultsDialogProps> = ({
  open,
  onClose,
  entityType,
  entityName,
  operationType,
  reservationResult
}) => {
  const [successExpanded, setSuccessExpanded] = useState(false);

  if (!reservationResult) {
    return null;
  }

  const entityLabel = entityType === 'session' 
    ? 'Coaching Session' 
    : entityType === 'program' || entityType === 'coachingProgram'
    ? 'Coaching Program'
    : 'Group Play';
  const operationLabel = operationType === 'create' 
    ? 'created' 
    : operationType === 'rebook'
    ? 'rebooked'
    : 'updated';
  const hasFailures = reservationResult.totalFailed > 0;
  const hasSuccesses = reservationResult.totalSuccessful > 0;

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only allow closing via the Close button, not by clicking backdrop or pressing ESC
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6">
            {entityLabel} {operationLabel} successfully!
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <strong>"{entityName}"</strong> has been {operationLabel} successfully.
          </Alert>

          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            📍 Venue Reservations
          </Typography>

          {/* Success Summary */}
          {hasSuccesses && (
            <Box sx={{ mb: 2 }}>
              <Accordion 
                expanded={successExpanded} 
                onChange={() => setSuccessExpanded(!successExpanded)}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'success.main',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography>
                      Successfully reserved <strong>{reservationResult.totalSuccessful}</strong>
                      {hasFailures && ` of ${reservationResult.totalAttempted}`} venue slot{reservationResult.totalSuccessful !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {reservationResult.successes && reservationResult.successes.length > 0 ? (
                      reservationResult.successes.map((success, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={success.amenityName}
                            secondary={`${formatDate(success.date)} at ${formatTimeString(success.startTime)} - ${formatTimeString(success.endTime)}`}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem>
                        <ListItemText
                          primary={`${reservationResult.totalSuccessful} reservation${reservationResult.totalSuccessful !== 1 ? 's' : ''} created`}
                          secondary="Details not available"
                        />
                      </ListItem>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {/* Failure Summary */}
          {hasFailures && (
            <Box>
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Failed to reserve {reservationResult.totalFailed} venue slot{reservationResult.totalFailed !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  The following venues could not be automatically reserved. You will need to manually book these venues.
                </Typography>

                <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1 }}>
                  {reservationResult.failures.map((failure, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider sx={{ my: 1 }} />}
                      <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                          <ErrorIcon color="error" fontSize="small" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {failure.amenityName}
                          </Typography>
                          <Chip 
                            label="Failed" 
                            size="small" 
                            color="error" 
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                          {formatDate(failure.date)} at {formatTimeString(failure.startTime)} - {formatTimeString(failure.endTime)}
                        </Typography>
                        <Typography variant="body2" color="error" sx={{ ml: 4, mt: 0.5 }}>
                          <strong>Reason:</strong> {failure.reason}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Alert>

              <Alert severity="info" icon={<WarningIcon />}>
                <Typography variant="body2">
                  <strong>Action Required:</strong> You must manually book the failed venue slots through the Courts Calendar or amenity booking system.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* No reservations attempted */}
          {!hasSuccesses && !hasFailures && (
            <Alert severity="info">
              No venue reservations were attempted.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReservationResultsDialog;
